import { useEffect } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import Lenis from "lenis";
import { Toaster } from "sonner";
import Home from "@/pages/Home";
import ServicesPage from "@/pages/ServicesPage";
import ServiceDetailPage from "@/pages/ServiceDetailPage";
import WorkPage from "@/pages/WorkPage";
import AboutPage from "@/pages/AboutPage";
import ContactPage from "@/pages/ContactPage";
import InsightsPage from "@/pages/InsightsPage";
import PostPage from "@/pages/PostPage";
import PortfolioPage from "@/pages/PortfolioPage";
import IndustryPage from "@/pages/IndustryPage";
import LocationsPage from "@/pages/LocationsPage";
import Admin from "@/pages/Admin";
import { CustomCursor } from "@/components/CustomCursor";

const ScrollToTop = () => {
  const { pathname } = useLocation();
  useEffect(() => {
    if (window.__lenis) window.__lenis.scrollTo(0, { immediate: true });
    else window.scrollTo(0, 0);
  }, [pathname]);
  return null;
};

const PageWrap = ({ children }) => (
  <motion.div
    initial={{ opacity: 0 }}
    animate={{ opacity: 1 }}
    exit={{ opacity: 0 }}
    transition={{ duration: 0.35 }}
  >
    {children}
  </motion.div>
);

const Curtain = () => {
  const { pathname } = useLocation();
  return (
    <AnimatePresence mode="wait">
      <motion.div
        key={pathname}
        aria-hidden="true"
        className="fixed inset-0 z-[95] pointer-events-none bg-[#07070c] flex items-center justify-center"
        style={{ transformOrigin: "top" }}
        initial={{ scaleY: 1 }}
        animate={{ scaleY: 0, transition: { duration: 0.7, ease: [0.76, 0, 0.24, 1], delay: 0.2 } }}
        exit={{ scaleY: 1, transition: { duration: 0.5, ease: [0.76, 0, 0.24, 1] } }}
      >
        <img src="/images/logo-mark.png" alt="" className="w-10 opacity-80" />
        <div className="absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r from-[#6D00FF] via-[#C43CFF] to-[#6D00FF]" />
      </motion.div>
    </AnimatePresence>
  );
};

const AnimatedRoutes = () => {
  const location = useLocation();
  return (
    <AnimatePresence mode="wait">
      <Routes location={location} key={location.pathname}>
        <Route path="/" element={<PageWrap><Home /></PageWrap>} />
        <Route path="/services" element={<PageWrap><ServicesPage /></PageWrap>} />
        <Route path="/services/:slug" element={<PageWrap><ServiceDetailPage /></PageWrap>} />
        <Route path="/work" element={<PageWrap><WorkPage /></PageWrap>} />
        <Route path="/about" element={<PageWrap><AboutPage /></PageWrap>} />
        <Route path="/contact" element={<PageWrap><ContactPage /></PageWrap>} />
        <Route path="/insights" element={<PageWrap><InsightsPage /></PageWrap>} />
        <Route path="/insights/:slug" element={<PageWrap><PostPage /></PageWrap>} />
        <Route path="/portfolio" element={<PageWrap><PortfolioPage /></PageWrap>} />
        <Route path="/industries/:slug" element={<PageWrap><IndustryPage /></PageWrap>} />
        <Route path="/locations" element={<PageWrap><LocationsPage /></PageWrap>} />
        <Route path="/admin" element={<PageWrap><Admin /></PageWrap>} />
      </Routes>
    </AnimatePresence>
  );
};

function App() {
  useEffect(() => {
    // Lenis smooth-wheel only makes sense with a precise pointer; on touch devices
    // native scrolling is already momentum-based and Lenis causes jank/sticking.
    if (!window.matchMedia("(pointer: fine)").matches) return undefined;
    const lenis = new Lenis({ duration: 1.15, smoothWheel: true });
    window.__lenis = lenis;
    let raf;
    const loop = (time) => {
      lenis.raf(time);
      raf = requestAnimationFrame(loop);
    };
    raf = requestAnimationFrame(loop);
    return () => {
      cancelAnimationFrame(raf);
      lenis.destroy();
      window.__lenis = null;
    };
  }, []);

  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Curtain />
        <AnimatedRoutes />
      </BrowserRouter>
      <CustomCursor />
      <Toaster
        theme="dark"
        position="bottom-right"
        toastOptions={{
          style: {
            background: "#0a0a10",
            border: "1px solid rgba(139,44,255,0.35)",
            color: "#ffffff",
          },
        }}
      />
    </div>
  );
}

export default App;
